import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { fadeAnimation } from 'src/animations';
import { Product } from 'src/app/lib/product/classes';
import { ProductService } from 'src/app/lib/product/services/product.service';

@Component({
  selector: 'app-select-product-form',
  templateUrl: './select-product-form.component.html',
  styleUrls: ['./select-product-form.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectProductFormComponent),
      multi: true
    }
  ],
  animations: [ fadeAnimation ]
})
export class SelectProductFormComponent implements ControlValueAccessor, OnDestroy, OnInit {

  @Input() useMaterialInput: boolean = false;

  @Output() productChanged: EventEmitter<Product> = new EventEmitter();

  appearance: 'fill' | 'outline' = 'fill';

  initialValue = undefined;

  formGroup: FormGroup;

  inputFocussed: BehaviorSubject<boolean> = new BehaviorSubject(false);

  keyupDebounceSub: Subscription = null;
  currentSearchSub: Subscription = null;

  currentProduct: Product = null;
  products: Product[] = [];

  @ViewChild('input', { static: false }) public input: ElementRef<HTMLInputElement>;

  private _subscriptions: Subscription[] = [];

  constructor(
    private _productService: ProductService,
    private _formBuilder: FormBuilder
  ) {
    this.formGroup = this._formBuilder.group({
      'filter': null,
      'value': null
    });
  }

  clearValue(event?){
    if(event){
      event.preventDefault();
      event.stopPropagation();
    }
    this.setProduct(null);
  }

  displayProduct(product: Product): string {
    if(!product) return '';
    return `${product.productnumber? product.productnumber: ''} ${product.productservice? product.productservice: ''}`;
  }

  focus(){
    this
      .input
      .nativeElement
      .focus();
  }

  ngOnDestroy(): void {
    for(let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      this.filterControl.valueChanges.pipe(distinctUntilChanged()).subscribe(
          () => {
            if (this.currentSearchSub) this.currentSearchSub.unsubscribe();
            this.currentSearchSub = null;
            this.keyupDebounceSub = timer(2000).subscribe(() => this.searchProduct());
          }
        ),
      this.valueControl
        .valueChanges
        .subscribe(
          () => {
            this.setCurrentProduct();
            this.productChanged
              .emit(this.currentProduct);
          }
        ),
      this.inputFocussed
        .subscribe((value: boolean) => {
          this.filterControl.setValue(!value && this.currentProduct? this.displayProduct(this.currentProduct): null, {
            'emitEvent': false
          });
        })
    ]);
  }

  onBlur(event?: Event) {
    if(event){
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    this.inputFocussed.next(false);
  }

  onFocus(event) {
    if(event) event.preventDefault();
    this.inputFocussed.next(true);
  }

  onSearchClicked(event) {
    if (event){
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.currentSearchSub) this.currentSearchSub.unsubscribe();
    this.currentSearchSub = null;
    this.searchProduct();
  }

  public onTouched: () => void = () => { };

  registerOnChange(fn: any): void {
    this
      ._subscriptions
      .push(
        this
          .valueControl
          .valueChanges
          .subscribe(fn)
      );
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  searchProduct() {
    if (this.keyupDebounceSub) this.keyupDebounceSub.unsubscribe();
    this.keyupDebounceSub = null;

    this.currentSearchSub = this._productService.getAllProducts(true).subscribe((products: Product[]) => {
      let filtered = products.filter(x => {
        if(typeof this.filterControl.value !== 'string') return true;
        let normalized = this
          .filterControl
          .value
          .trim()
          .toLocaleLowerCase();
        if(normalized.length === 0) return true;
        if(typeof(x.productdesc) === 'string' && x.productdesc.toLocaleLowerCase().includes(normalized)) return true;
        if(typeof(x.productservice) === 'string' && x.productservice.toLocaleLowerCase().includes(normalized)) return true;
        if(typeof(x.productnumber) === 'string' && x.productnumber.toLocaleLowerCase().includes(normalized)) return true;
        if(typeof(x.productdesc) === 'string' && x.productdesc.toLocaleLowerCase().includes(normalized)) return true;
        return false;
      });

      this.products = filtered;
    });
  }

  setProduct(product?: Product) {
    product? this.valueControl.setValue(product.productid): this.valueControl.setValue(null, { 'emitValue': false });
    this.setCurrentProduct();
    this
      .input
      .nativeElement
      .blur();
    this
      .inputFocussed
      .next(false);
  }

  setCurrentProduct(){
    if(this.valueControl.value){
      this._productService.getProductById(this.valueControl.value, true).subscribe((product: Product) => {
        this.currentProduct = product;
        if(!this.inputFocussed.value) this.filterControl.setValue(this.displayProduct(product));
      });
    } else this.currentProduct = null;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  writeValue(val: any): void {
    this
      .valueControl
      .setValue(val);

    if (typeof this.initialValue === 'undefined') this.initialValue = val;
  }

  get filterControl(): AbstractControl {
    return this.formGroup.controls['filter'];
  }

  get valueControl(): AbstractControl {
    return this.formGroup.controls['value'];
  }

}
